/**
 * Reads amount, date, merchant and a payment hint from OCR text of an Italian receipt or a payment
 * screenshot. Pure and deterministic. Every value is a prediction with a confidence: "high" may
 * pre-fill an empty form field, "low" is only offered as a suggestion (ARCHITECTURE.md §16).
 */

export type Confidence = "high" | "low";

export interface ReadField<T> {
  value: T;
  confidence: Confidence;
  /** The OCR line the value was read from, shown to the user for checking. */
  source: string;
}

export type PaymentHint = "cash" | "card" | "satispay" | "postepay";

export interface ReceiptReading {
  /** Decimal string with a dot, two decimals (`"23.40"`). */
  amount: ReadField<string> | null;
  /** `YYYY-MM-DD`. */
  date: ReadField<string> | null;
  merchant: ReadField<string> | null;
  /** Never pre-filled: the payment method decides the accounting month. */
  payment: ReadField<PaymentHint> | null;
}

export interface OcrTextLine {
  text: string;
  confidence?: number;
}

/** Below this Tesseract line confidence a value is never "high". */
const MIN_LINE_CONFIDENCE = 55;
const MAX_AMOUNT = 100_000;
const MAX_AGE_DAYS = 400;

// ─── Text helpers ───────────────────────────────────────────────────────────────────────────────

const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase();

/** Repairs common OCR misreads inside numeric tokens ("2O,4O" → "20,40", "l2/09" → "12/09"). */
function repairDigits(line: string): string {
  let s = line.replace(/(\d)\s*([,.])\s*(\d{2})(?!\d)/g, "$1$2$3"); // "23 ,40" / "23, 40"
  s = s.replace(/[0-9OoIlSB€.,:/-]+/g, (token) => {
    const digits = (token.match(/\d/g) ?? []).length;
    const letters = (token.match(/[OoIlSB]/g) ?? []).length;
    // Only tokens that are clearly numbers with a few misread characters.
    if (digits === 0 || letters === 0 || letters > digits || !/[.,/:-]/.test(token)) return token;
    return token.replace(/[Oo]/g, "0").replace(/[Il]/g, "1").replace(/S/g, "5").replace(/B/g, "8");
  });
  return s;
}

const AMOUNT_RE = /(?<![\d.,])(\d{1,3}(?:\.\d{3})+,\d{2}|\d{1,3}(?:,\d{3})+\.\d{2}|\d+[.,]\d{2})(?![.,]?\d)(?!\s*%)/g;

function toDecimal(token: string): string | null {
  let s = token;
  if (/^\d{1,3}(\.\d{3})+,\d{2}$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(,\d{3})+\.\d{2}$/.test(s)) s = s.replace(/,/g, "");
  else s = s.replace(",", ".");
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0 || n >= MAX_AMOUNT) return null;
  return n.toFixed(2);
}

function amountsIn(line: string): string[] {
  return [...repairDigits(line).matchAll(AMOUNT_RE)].map((m) => toDecimal(m[1]!)).filter((v): v is string => v !== null);
}

// ─── Amount ─────────────────────────────────────────────────────────────────────────────────────

const NOT_A_TOTAL = /SUBTOTAL|\bRESTO\b|\bIVA\b|IMPOSTA|SCONTO|NON RISCOSS|\bCONTANT[EI]\b|RESIDUO|SALDO|DISPONIBIL|PUNTI|BUONO|ARROTONDAMENTO|\bRESO\b/;
const TOTAL = /\bTOTALE\b|\bTOT\b|\bTOTAL\b|\bTOT\./;
const PAID = /IMPORTO|PAGAMENTO\s+(ELETTRONICO|CARTA|BANCOMAT|POS|CON)|\bPAGATO\b|ADDEBIT|\bSPESA\b|TRANSAZIONE/;
const CURRENCY = /€|\bEUR\b|\bEURO\b/;

interface AmountCandidate {
  value: string;
  rank: number;
  source: string;
  confidence: number;
}

function readAmount(lines: OcrTextLine[]): ReadField<string> | null {
  const candidates: AmountCandidate[] = [];
  lines.forEach((line, i) => {
    const up = fold(line.text);
    const confidence = line.confidence ?? 100;
    if (NOT_A_TOTAL.test(up)) return;
    const rank = TOTAL.test(up) ? 3 : PAID.test(up) ? 2 : CURRENCY.test(up) ? 1 : 0;
    let values = amountsIn(line.text);
    let source = line.text;
    // "TOTALE EURO" on one line and "23,40" alone on the next.
    if (values.length === 0 && rank >= 2) {
      const next = lines[i + 1];
      if (next && !NOT_A_TOTAL.test(fold(next.text)) && amountsIn(next.text).length === 1 && fold(next.text).replace(/[\d.,€\sEUR-]/g, "").length <= 2) {
        values = amountsIn(next.text);
        source = `${line.text} ${next.text}`;
      }
    }
    if (values.length === 0) return;
    // A keyword line with several amounts (e.g. "TOTALE 2 ARTICOLI 23,40"): the last one is the total.
    if (rank >= 2) values = [values[values.length - 1]!];
    for (const value of values) candidates.push({ value, rank, source, confidence });
  });
  if (candidates.length === 0) return null;

  const best = Math.max(...candidates.map((c) => c.rank));
  const top = candidates.filter((c) => c.rank === best);
  const distinct = [...new Set(top.map((c) => c.value))];
  // Several different totals: the most frequent, then the largest.
  const count = (v: string) => top.filter((c) => c.value === v).length;
  const value = distinct.sort((a, b) => count(b) - count(a) || Number(b) - Number(a))[0]!;
  const chosen = top.find((c) => c.value === value)!;

  const agreesWithPaid = best === 3 && candidates.some((c) => c.rank === 2 && c.value === value);
  let confidence: Confidence = "low";
  if (best === 3 && (distinct.length === 1 || agreesWithPaid)) confidence = "high";
  else if (best === 2 && distinct.length === 1) confidence = "high";
  else if (best === 1 && new Set(candidates.filter((c) => c.rank >= 1).map((c) => c.value)).size === 1) confidence = "high"; // screenshot: one € amount
  if (chosen.confidence < MIN_LINE_CONFIDENCE) confidence = "low";
  return { value, confidence, source: chosen.source };
}

// ─── Date ───────────────────────────────────────────────────────────────────────────────────────

const MONTHS: Record<string, number> = { GEN: 1, FEB: 2, MAR: 3, APR: 4, MAG: 5, GIU: 6, LUG: 7, AGO: 8, SET: 9, OTT: 10, NOV: 11, DIC: 12 };
const NUMERIC_DATE = /(?<!\d)(\d{1,2})\s?[/\-.]\s?(\d{1,2})\s?[/\-.]\s?(\d{4}|\d{2})(?!\d)/g;
const HAS_DATE = new RegExp(NUMERIC_DATE.source); // non-global: safe for .test()
const NAMED_DATE = /(?<!\d)(\d{1,2})\s+(GEN|FEB|MAR|APR|MAG|GIU|LUG|AGO|SET|OTT|NOV|DIC)[A-Z]*\.?(?:\s+(\d{4}))?/g;

function isoDate(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCMonth() !== m - 1) return null; // 31/02
  return date.toISOString().slice(0, 10);
}

const dayDiff = (a: string, b: string) => (Date.parse(a) - Date.parse(b)) / 86_400_000;

function readDate(lines: OcrTextLine[], today: string): ReadField<string> | null {
  const found: { value: string; source: string; labelled: boolean; confidence: number }[] = [];
  const inRange = (iso: string) => dayDiff(iso, today) <= 1 && dayDiff(today, iso) <= MAX_AGE_DAYS;
  for (const line of lines) {
    const text = fold(repairDigits(line.text));
    const labelled = /\bDATA\b|\bDEL\b|\bIL\b/.test(text);
    for (const m of text.matchAll(NUMERIC_DATE)) {
      const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
      const iso = isoDate(year, Number(m[2]), Number(m[1]));
      if (iso && inRange(iso)) found.push({ value: iso, source: line.text, labelled, confidence: line.confidence ?? 100 });
    }
    for (const m of text.matchAll(NAMED_DATE)) {
      const month = MONTHS[m[2]!]!;
      const day = Number(m[1]);
      let iso = isoDate(m[3] ? Number(m[3]) : Number(today.slice(0, 4)), month, day);
      // "28 set" without a year: this year, or last year if that would be in the future.
      if (iso && !m[3] && dayDiff(iso, today) > 1) iso = isoDate(Number(today.slice(0, 4)) - 1, month, day);
      if (iso && inRange(iso)) found.push({ value: iso, source: line.text, labelled, confidence: line.confidence ?? 100 });
    }
  }
  if (found.length === 0) return null;
  const distinct = [...new Set(found.map((f) => f.value))];
  const labelled = [...new Set(found.filter((f) => f.labelled).map((f) => f.value))];
  const pick = labelled.length === 1 ? found.find((f) => f.labelled)! : found[0]!;
  let confidence: Confidence = distinct.length === 1 || labelled.length === 1 ? "high" : "low";
  if (pick.confidence < MIN_LINE_CONFIDENCE) confidence = "low";
  return { value: pick.value, confidence, source: pick.source };
}

// ─── Merchant ───────────────────────────────────────────────────────────────────────────────────

const HEADER_END = /P\.?\s*IVA|PARTITA\s+IVA|\bC\.?\s?F\.?\b|CODICE FISCALE|DOCUMENTO COMMERCIALE|SCONTRINO|\bTEL\b|TELEFONO|^VIA\b|\bVIA\s|VIALE|CORSO\s|PIAZZA|P\.ZZA|LARGO\s|\b\d{5}\b/;
const NOT_A_NAME = /BENVENUT|WELCOME|GRAZIE|ARRIVEDERCI|DOCUMENTO|VENDITA|PRESTAZIONE|COPIA|CLIENTE|CASSA|OPERATORE|\bRT\b|ORARIO|APERTO/;
const SCREENSHOT_MERCHANT = /(?:ESERCENTE|PAGATO A|PAGAMENTO A|BENEFICIARIO|DESTINATARIO|PRESSO)\s*:?\s*(.*)$/;
const LEGAL_FORM = /\b(S\.?\s?P\.?\s?A|S\.?\s?R\.?\s?L(?:\.?\s?S)?|S\.?\s?N\.?\s?C|S\.?\s?A\.?\s?S|SOC\.?\s?COOP|COOP\.?\s?A\s?R\.?\s?L)\.?(?=\s|$)/g;

function letters(s: string): number {
  return (s.match(/[A-Za-zÀ-ÿ]/g) ?? []).length;
}

/** "CONAD CITY S.P.A." → "Conad City". */
export function cleanMerchantName(raw: string): string {
  const cleaned = fold(raw)
    .replace(LEGAL_FORM, " ")
    .replace(/[^A-Z0-9&'\s.-]/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^[\s.'-]+|[\s.'-]+$/g, "")
    .trim();
  return cleaned.toLowerCase().replace(/(^|[\s'&.-])([a-z])/g, (_, sep: string, ch: string) => sep + ch.toUpperCase());
}

function readMerchant(lines: OcrTextLine[]): ReadField<string> | null {
  // Payment screenshots: "Esercente: CONAD CITY", "Pagato a" + name on the next line.
  for (let i = 0; i < lines.length; i++) {
    const m = fold(lines[i]!.text).match(SCREENSHOT_MERCHANT);
    if (!m) continue;
    const rest = (m[1] ?? "").replace(AMOUNT_RE, " ").replace(NUMERIC_DATE, " ").trim();
    const raw = letters(rest) >= 3 ? rest : lines[i + 1]?.text ?? "";
    const name = cleanMerchantName(raw);
    if (letters(name) >= 3) {
      const ok = (lines[i]!.confidence ?? 100) >= MIN_LINE_CONFIDENCE;
      return { value: name, confidence: ok ? "high" : "low", source: lines[i]!.text };
    }
  }
  // Receipts: the first real name in the header, before VAT number / address / document title.
  const header = lines.slice(0, 10);
  const end = header.findIndex((l) => HEADER_END.test(fold(l.text)));
  for (let i = 0; i < header.length && (end === -1 || i < end); i++) {
    const line = header[i]!;
    const up = fold(line.text);
    if (letters(up) < 3 || letters(up) / up.replace(/\s/g, "").length < 0.6 || NOT_A_NAME.test(up)) continue;
    if (amountsIn(line.text).length || HAS_DATE.test(up)) continue;
    const name = cleanMerchantName(line.text);
    if (letters(name) < 3) continue;
    // High only when the usual receipt structure confirms it: name, then VAT number or address.
    const structured = end !== -1 && end - i <= 4;
    const confident = structured && (line.confidence ?? 100) >= MIN_LINE_CONFIDENCE;
    return { value: name, confidence: confident ? "high" : "low", source: line.text };
  }
  return null;
}

// ─── Payment hint ───────────────────────────────────────────────────────────────────────────────

function readPayment(lines: OcrTextLine[]): ReadField<PaymentHint> | null {
  const hits: { hint: PaymentHint; source: string }[] = [];
  for (const line of lines) {
    const up = fold(line.text);
    // "PAGAMENTO CONTANTE 0,00" on an electronic payment: not a hint.
    if (/(?<![\d.,])0[.,]00(?!\d)/.test(repairDigits(line.text)) && amountsIn(line.text).length === 0) continue;
    if (/SATISPAY/.test(up)) hits.push({ hint: "satispay", source: line.text });
    else if (/POSTEPAY/.test(up)) hits.push({ hint: "postepay", source: line.text });
    else if (/\bCONTANT[EI]\b/.test(up) && !/\bRESTO\b/.test(up)) hits.push({ hint: "cash", source: line.text });
    else if (/CARTA DI CREDITO|\bVISA\b|MASTERCARD|MAESTRO|BANCOMAT|PAGAMENTO ELETTRONICO|\bPOS\b|CONTACTLESS/.test(up)) hits.push({ hint: "card", source: line.text });
  }
  if (hits.length === 0) return null;
  for (const hint of ["satispay", "postepay", "cash", "card"] as const) {
    const hit = hits.find((h) => h.hint === hint);
    if (!hit) continue;
    const kinds = new Set(hits.map((h) => h.hint));
    // A card is ambiguous (Unicredit credit card or PostePay debit card): always low.
    return { value: hint, confidence: kinds.size === 1 && hint !== "card" ? "high" : "low", source: hit.source };
  }
  return null;
}

// ─── Entry point ────────────────────────────────────────────────────────────────────────────────

/**
 * @param text Full OCR text (used when `lines` is empty).
 * @param lines OCR lines with Tesseract confidence, in reading order.
 * @param today `YYYY-MM-DD`, to reject dates in the future or too old.
 */
export function parseReceipt(text: string, lines: readonly OcrTextLine[], { today }: { today: string }): ReceiptReading {
  const all = (lines.length ? lines : text.split(/\r?\n/).map((t) => ({ text: t })))
    .map((l) => ({ ...l, text: l.text.replace(/\s+/g, " ").trim() }))
    .filter((l) => l.text.length > 0);
  return { amount: readAmount(all), date: readDate(all, today), merchant: readMerchant(all), payment: readPayment(all) };
}
